import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { ToastColor, ToastPosition } from '../types';
import { COLORS } from '../styles';
import { Close } from '../icons';

const DropInAnimation = keyframes`
  0% {
    transform: translateY(-50px);
  }
  66% {
    transform: translateY(50px);
  }
  100% {
    transform: translateY(0);
  }
`;

const Container = styled.div<{ bg: string; color: string }>`
  background-color: ${props => props.bg};
  color: ${props => props.color};
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  font-size: 16px;
  animation: ${DropInAnimation} 300ms ease-in-out;
  min-height: 56px;
  padding: 2px 48px 2px 24px;
  margin-bottom: 8px;
  line-height: 19px;
  display: flex;
  align-items: center;
  position: relative;
  & a {
    color: ${props => props.color};
    font-weight: bold;
  }
`;

const ActionButton = styled.button<{ color: string }>`
  background-color: transparent;
  border: 2px solid ${props => props.color};
  border-radius: 4px;
  color: ${props => props.color};
  font-weight: bold;
  font-size: 19px;
  margin-left: 8px;
  cursor: pointer;

  &:last-child {
    margin-right: 18px;
  }
`;

const ActionButtons = styled.div`
  margin: 0 0 0 auto;

  display: flex;
  flex-direction: row;
`;

const CloseButton = styled.button<{ color: string }>`
  position: absolute;
  top: calc(50% - 11px);
  right: 12px;
  padding: 0;
  margin: 0;
  color: ${props => props.color};
  width: 24px;
  height: 24px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const CloseIcon = styled(Close)``;

export type ToastOptions = {
  color?: ToastColor;
  autoClose?: boolean;
  delay?: number;
  showCloseButton?: boolean;
  action?: {
    label: string;
    handler: () => void;
  }[];
  position: ToastPosition;
};

type Props = {
  remove?: any;
  content: any;
  options?: ToastOptions;
  testId?: string;
};

const DEFAULT_OPTIONS: ToastOptions = {
  color: ToastColor.BLACK,
  autoClose: true,
  delay: 5000,
  showCloseButton: false,
  position: ToastPosition.TOPLEFT,
};

const Toast = ({ content, remove, options = DEFAULT_OPTIONS, testId }: Props) => {
  const removeRef = React.useRef();
  removeRef.current = remove;

  const opts = { ...DEFAULT_OPTIONS, ...options };

  React.useEffect(() => {
    if (opts?.autoClose) {
      // @ts-ignore
      const id = setTimeout(() => removeRef.current(), opts.delay);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  let bg = COLORS.white;
  let color = COLORS.black;

  switch (opts.color) {
    case ToastColor.BLACK:
      bg = COLORS.neutral_800;
      color = COLORS.white;
      break;
    case ToastColor.WHITE:
      bg = COLORS.white;
      color = COLORS.black;
      break;
    case ToastColor.BLUE:
      bg = COLORS.toast_info;
      color = COLORS.white;
      break;
    case ToastColor.GREEN:
      bg = COLORS.toast_success;
      color = COLORS.white;
      break;
    case ToastColor.ORANGE:
      bg = COLORS.toast_warning;
      color = COLORS.white;
      break;
    case ToastColor.RED:
      bg = COLORS.toast_error;
      color = COLORS.white;
      break;
    default:
  }

  return (
    <Container bg={bg} color={color} data-testid={testId}>
      {content}
      {opts.action && (
        <ActionButtons>
          {opts.action.map(action => (
            <ActionButton
              color={color}
              onClick={() => {
                remove();
                action.handler();
              }}>
              {action.label}
            </ActionButton>
          ))}
        </ActionButtons>
      )}
      {opts.showCloseButton && (
        <CloseButton color={color} type="button" onClick={remove}>
          <CloseIcon size="24px" />
        </CloseButton>
      )}
    </Container>
  );
};

export default Toast;
